import { Command } from 'commander';
import chalk from 'chalk';
import { existsSync, readFileSync, writeFileSync, mkdirSync, unlinkSync, rmSync } from 'fs';
import { join, dirname, basename, isAbsolute, relative } from 'path';
import { execSync, spawnSync } from 'child_process';
import * as net from 'net';
import * as http from 'http';
import { logger } from '../lib/logger.js';
import {
  loadAppSettings,
  extractMergedConnectionString,
  extractMergedInitialAdmin,
  extractMergedCorsOrigins,
  extractMergedFrontendUrl,
  extractMergedSerilogFile,
  extractMergedFileStorageBasePath,
  resolveDevFileStorageBasePath,
  parseConnectionString,
  computeFilesMeta,
  diffFilesMeta,
  deepMerge,
  FileMeta,
} from '../lib/appsettings.js';
import { findFreePorts } from '../lib/port-scanner.js';
import {
  validateConfigureSpecObject,
  buildAppsettingsLocalPatch,
  applyLocalProfile,
  patchEnvContent,
  extractApiPort,
  ensureGitignoreLines,
  hasSqlSpec,
  rewritePortsDeep,
  profileServesHttps,
  deriveDatabaseName,
  generateStrongPassword,
  DEFAULT_ADMIN_EMAIL,
} from '../lib/dev-configure.js';
import { ensureDatabaseExists, type SqlProbeResult } from '../lib/sql-probe.js';
import { classifyOccupancy, parseListeningPorts, type SidecarPids } from '../lib/dev-occupancy.js';
import {
  decideNextAction,
  nextWaitInterval,
  buildNeedsInputEnvelope,
  buildSeedConfigEnvelope,
  listenPidBackfillTargets,
  type UpFlags,
  type OrchestratorState,
} from '../lib/dev-orchestrator.js';
import { buildDetachedLaunchPwsh } from '../lib/dev-launch.js';
import { detectApiDir, detectWebDir, findProjectRoot } from '../lib/dev-paths.js';
import {
  isServingStatus,
  buildSummary,
  renderSummary,
  type DevSummaryInput,
} from '../lib/dev-summary.js';

// ── Cache paths ────────────────────────────────────────────────────────────

// Anchor EVERY path to the project ROOT (found by walking up from cwd), never to
// cwd. A cwd-relative cache was THE cause of the recurring "No cache found": run
// `ss dev *` from `web/<app>` or `src/<App>.Api` (as happens mid-build) and the
// cache "vanished" → stop no-op'd → the prior servers lingered as zombies holding
// the ports → the next launch could not bind → readiness timed out. With the root
// anchor, every subcommand finds the same cache from anywhere in the tree.
const PROJECT_ROOT = findProjectRoot(process.cwd()) ?? process.cwd();
const DEV_RUN_CACHE = join(PROJECT_ROOT, '.claude', 'config', 'dev-run.json');
const DEV_RUN_PIDS = join(PROJECT_ROOT, '.claude', 'config', 'dev-run.pids.json');
const DEV_SESSION = join(PROJECT_ROOT, '.claude', 'dev-session.json');
const LAUNCHER_LOG_DIR = join(PROJECT_ROOT, '.claude', 'logs', 'dev-start');
const CACHE_MAX_AGE_DAYS = 7;

/** Sent on every dev probe — the API middleware rejects requests with no User-Agent (HTTP 400). */
const DEV_PROBE_USER_AGENT = 'SmartStack-ss-dev';

/** Shorten an absolute path to a project-relative, forward-slashed form for the cartouche
 *  (kept narrow so it fits INSIDE the box). Falls back to the abs path if it escapes the root. */
function toProjectRel(abs: string): string {
  try {
    const r = relative(PROJECT_ROOT, abs);
    return r && !r.startsWith('..') && !isAbsolute(r) ? r.replace(/\\/g, '/') : abs.replace(/\\/g, '/');
  } catch {
    return abs.replace(/\\/g, '/');
  }
}

// ── PID sidecar (servers launched detached by `ss dev start`) ────────────────
// Kept separate from dev-run.json (which `detect` fully overwrites). Lets
// `ss dev stop` clean up a server that crashed at boot and never bound its
// port (invisible to the netstat-based getListeningPid).

interface PidRecord {
  /** WMI Win32_Process.Create PID (the cmd.exe wrapper) — used for tree-kill. */
  wrapperPid: number;
  /** The dotnet/node child that actually bound the port (captured post-launch). null = not captured. */
  listenPid: number | null;
  port: number;
  started_at: string;
  log_out: string;
  log_err: string;
}

interface PidFile {
  version: number;
  backend?: PidRecord;
  frontend?: PidRecord;
}

/**
 * Read a (possibly legacy v1) sidecar record into the v2 shape. v1 stored a single
 * `pid` (the wrapper) and no `listenPid`; mapping it to `{ wrapperPid: pid,
 * listenPid: null }` lets the classifier's PORT fallback (used only by detect)
 * still recognise a pre-upgrade running env, so we never regress into a spurious
 * port-change prompt. A `listenPid` only ever comes from a launch WE just did —
 * never back-filled from an observation (that could adopt a foreign process that
 * grabbed our port).
 */
function normalizePidRecord(rec: unknown): PidRecord | undefined {
  if (!rec || typeof rec !== 'object') return undefined;
  const r = rec as Record<string, unknown>;
  const port = typeof r.port === 'number' ? r.port : NaN;
  if (Number.isNaN(port)) return undefined;
  const base = {
    port,
    started_at: typeof r.started_at === 'string' ? r.started_at : '',
    log_out: typeof r.log_out === 'string' ? r.log_out : '',
    log_err: typeof r.log_err === 'string' ? r.log_err : '',
  };
  if (typeof r.wrapperPid === 'number') {
    return { ...base, wrapperPid: r.wrapperPid, listenPid: typeof r.listenPid === 'number' ? r.listenPid : null };
  }
  if (typeof r.pid === 'number') {
    return { ...base, wrapperPid: r.pid, listenPid: null }; // legacy v1
  }
  return undefined;
}

/** Read the sidecar file (v1 or v2) normalised to the current shape, or null. */
function readPidFile(): PidFile | null {
  const raw = readJsonFile<{ backend?: unknown; frontend?: unknown }>(DEV_RUN_PIDS);
  if (!raw) return null;
  const pf: PidFile = { version: 2 };
  const b = normalizePidRecord(raw.backend);
  const f = normalizePidRecord(raw.frontend);
  if (b) pf.backend = b;
  if (f) pf.frontend = f;
  return pf;
}

/** Project a PidFile into the pure classifier's SidecarPids view. */
function toSidecarPids(pf: PidFile | null): SidecarPids | null {
  if (!pf) return null;
  const out: SidecarPids = {};
  if (pf.backend) out.backend = { listenPid: pf.backend.listenPid, port: pf.backend.port, wrapperPid: pf.backend.wrapperPid };
  if (pf.frontend) out.frontend = { listenPid: pf.frontend.listenPid, port: pf.frontend.port, wrapperPid: pf.frontend.wrapperPid };
  return out;
}

/**
 * Drop sidecar records whose captured child (`listenPid`) is no longer the live
 * listener — our server died (and maybe a foreigner took the port). Records with
 * `listenPid === null` are KEPT (a boot may still be racing; detect's port fallback
 * decides). Returns whether anything changed (the caller persists or unlinks).
 */
function pruneDeadSidecar(pf: PidFile | null): boolean {
  if (!pf) return false;
  let mutated = false;
  for (const side of ['backend', 'frontend'] as const) {
    const rec = pf[side];
    if (!rec) continue;
    if (rec.listenPid !== null && getListeningPid(rec.port) !== rec.listenPid) {
      delete pf[side];
      mutated = true;
    }
  }
  return mutated;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function readJsonFile<T>(filePath: string): T | null {
  try {
    if (!existsSync(filePath)) return null;
    return JSON.parse(readFileSync(filePath, 'utf-8')) as T;
  } catch {
    return null;
  }
}

function writeJsonFile(filePath: string, data: unknown): void {
  const dir = dirname(filePath);
  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true });
  }
  writeFileSync(filePath, JSON.stringify(data, null, 2), 'utf-8');
}

function testTcpConnection(host: string, port: number): Promise<'ok' | 'warning'> {
  return new Promise((resolve) => {
    const socket = new net.Socket();
    const timer = setTimeout(() => {
      socket.destroy();
      resolve('warning');
    }, 1000);

    socket.on('connect', () => {
      clearTimeout(timer);
      socket.destroy();
      resolve('ok');
    });

    socket.on('error', () => {
      clearTimeout(timer);
      resolve('warning');
    });

    try {
      socket.connect(port, host);
    } catch {
      clearTimeout(timer);
      resolve('warning');
    }
  });
}

// detectApiDir / detectWebDir / findProjectRoot now live in ../lib/dev-paths.ts
// (pure + unit-tested), parameterized by a base dir so detection works from any
// subfolder by walking up to the project root.

interface LaunchProfileInfo {
  port: number;
  profile: string;
  applicationUrl: string;
}

function pickLaunchProfile(launchSettingsPath: string, preferLocal: boolean): LaunchProfileInfo | null {
  try {
    const content = JSON.parse(readFileSync(launchSettingsPath, 'utf-8'));
    const profiles = content?.profiles || {};
    const order = preferLocal ? ['Local', 'http', 'https'] : ['http', 'https', 'Local'];

    const tryProfile = (name: string): LaunchProfileInfo | null => {
      const profile = profiles[name];
      if (!profile) return null;
      const urls: string = profile.applicationUrl || profile.launchUrl || '';
      // Prefer the plain-HTTP port (what the frontend + probes use), not the
      // first :port — the Local profile is written https-first, so a naive match
      // would mistake the https port for the API port (→ phantom mismatch).
      const port = extractApiPort(urls);
      if (port === null) return null;
      return { port, profile: name, applicationUrl: urls };
    };

    for (const name of order) {
      const r = tryProfile(name);
      if (r) return r;
    }

    const first = Object.entries(profiles)[0];
    if (first) {
      const [name] = first as [string, Record<string, string>];
      const r = tryProfile(name);
      if (r) return r;
    }
  } catch { /* ignore */ }
  return null;
}

function getEnvFiles(webDir: string): string[] {
  return ['.env.standalone', '.env.development', '.env.local', '.env']
    .map((f) => join(webDir, f))
    .filter((p) => existsSync(p));
}

function extractWebPort(webDir: string): { port: number; source: string | null } {
  for (const envPath of getEnvFiles(webDir)) {
    const content = readFileSync(envPath, 'utf-8');
    const match = content.match(/^VITE_PORT\s*=\s*(\d+)/m);
    if (match) return { port: parseInt(match[1], 10), source: basename(envPath) };
  }
  return { port: 5173, source: null };
}

/**
 * Detect a `--port <n>` flag hardcoded in package.json `scripts.local` / `scripts.dev`.
 * Such a flag OVERRIDES `VITE_PORT`, so the frontend binds a port `ss dev` is blind
 * to (the PickEBike `--port 2022` trace) → a phantom "frontend DOWN" + a stale config.
 * Read-only: we report it as a warning, never edit the client's package.json.
 */
function detectHardcodedVitePort(webDir: string): { port: number; script: string } | null {
  const pkgPath = join(webDir, 'package.json');
  if (!existsSync(pkgPath)) return null;
  try {
    const pkg = JSON.parse(readFileSync(pkgPath, 'utf-8'));
    const scripts: Record<string, unknown> = pkg?.scripts ?? {};
    for (const name of ['local', 'dev']) {
      const cmd = scripts[name];
      if (typeof cmd !== 'string') continue;
      const m = cmd.match(/--port[=\s]+(\d{2,5})/);
      if (m) return { port: parseInt(m[1], 10), script: name };
    }
  } catch { /* ignore */ }
  return null;
}

function extractViteApiUrl(webDir: string): { url: string | null; source: string | null } {
  for (const envPath of getEnvFiles(webDir)) {
    const content = readFileSync(envPath, 'utf-8');
    const match = content.match(/^VITE_API_URL\s*=\s*(.+)/m);
    if (match) return { url: match[1].trim(), source: basename(envPath) };
  }
  return { url: null, source: null };
}

function getFrontendCmd(webDir: string): { cmd: string; source: string } {
  const pkgPath = join(webDir, 'package.json');
  if (existsSync(pkgPath)) {
    try {
      const pkg = JSON.parse(readFileSync(pkgPath, 'utf-8'));
      if (pkg?.scripts?.local) return { cmd: 'npm run local', source: 'package.json:scripts.local' };
      if (pkg?.scripts?.dev) return { cmd: 'npm run dev', source: 'package.json:scripts.dev' };
    } catch { /* ignore */ }
  }
  return { cmd: 'npm run dev', source: 'default' };
}

// One `netstat -ano` snapshot per CLI invocation (module-level — each `ss dev`
// subcommand is a fresh process, so this is naturally scoped to a single command).
// The old code spawned a FULL netstat for EVERY port lookup → 6-8 spawns per
// /dev-start; now it's one per subcommand (the poll after launch refreshes on demand).
let _netstatSnapshot: Map<number, number> | null = null;

function refreshNetstatSnapshot(): void {
  try {
    const out = execSync('netstat -ano 2>nul', { encoding: 'utf-8', timeout: 3000 });
    _netstatSnapshot = parseListeningPorts(out);
  } catch {
    _netstatSnapshot = new Map();
  }
}

/**
 * PID listening on `port`, or null. Reads the cached snapshot; pass `refresh=true`
 * to force a fresh netstat (the post-launch listenPid poll genuinely needs live reads).
 */
function getListeningPid(port: number, refresh = false): number | null {
  if (refresh || _netstatSnapshot === null) refreshNetstatSnapshot();
  return _netstatSnapshot!.get(port) ?? null;
}

function detectModeMismatch(
  apiPort: number,
  launchProfile: string,
  viteApiUrl: string | null,
  viteSource: string | null
): string | null {
  if (!viteApiUrl) return null;
  const portMatch = viteApiUrl.match(/:(\d{4,5})/);
  if (!portMatch) return null;
  const vitePort = parseInt(portMatch[1], 10);
  if (vitePort !== apiPort) {
    return `mode mismatch: backend launch profile "${launchProfile}" → API port ${apiPort}, but ${viteSource} declares VITE_API_URL=${viteApiUrl} (:${vitePort})`;
  }
  return null;
}

interface MismatchInfo {
  api_port: number;
  vite_port: number;
  launch_profile: string;
  vite_source: string | null;
}

/** Structured form of detectModeMismatch (the string version stays in warnings[]). */
function buildMismatchInfo(
  apiPort: number,
  launchProfile: string,
  viteApiUrl: string | null,
  viteSource: string | null
): MismatchInfo | null {
  if (!viteApiUrl) return null;
  const m = viteApiUrl.match(/:(\d{4,5})/);
  if (!m) return null;
  const vitePort = parseInt(m[1], 10);
  if (vitePort === apiPort) return null;
  return { api_port: apiPort, vite_port: vitePort, launch_profile: launchProfile, vite_source: viteSource };
}

/** Current git branch, or null if not a repo / detached HEAD. */
function getGitBranch(): string | null {
  try {
    const out = execSync('git rev-parse --abbrev-ref HEAD', {
      encoding: 'utf-8',
      timeout: 2000,
      stdio: ['ignore', 'pipe', 'ignore'],
    }).trim();
    return out && out !== 'HEAD' ? out : null;
  } catch {
    return null;
  }
}

function isDefaultBranch(branch: string | null): boolean {
  return branch === null || ['main', 'master', 'develop'].includes(branch);
}

interface HealthcheckResult {
  status: number;
  ok: boolean;
  bodySnippet: string;
  error?: string;
}

function attemptLogin(apiPort: number, email: string, password: string): Promise<HealthcheckResult> {
  return new Promise((resolve) => {
    const payload = JSON.stringify({ email, password });
    const req = http.request(
      {
        host: 'localhost',
        port: apiPort,
        path: '/api/auth/login',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Content-Length': Buffer.byteLength(payload),
          'User-Agent': DEV_PROBE_USER_AGENT,
        },
        timeout: 8000,
      },
      (res) => {
        let body = '';
        res.on('data', (chunk) => {
          body += chunk;
          if (body.length > 512) body = body.slice(0, 512);
        });
        res.on('end', () => {
          resolve({
            status: res.statusCode || 0,
            ok: (res.statusCode || 0) >= 200 && (res.statusCode || 0) < 300,
            bodySnippet: body,
          });
        });
      }
    );
    req.on('error', (err) => {
      resolve({ status: 0, ok: false, bodySnippet: '', error: err.message });
    });
    req.on('timeout', () => {
      req.destroy();
      resolve({ status: 0, ok: false, bodySnippet: '', error: 'timeout' });
    });
    req.write(payload);
    req.end();
  });
}

// ── Command: ss dev ────────────────────────────────────────────────────────

export const devCommand = new Command('dev')
  .description('SmartStack dev environment management');

devCommand.action(() => {
  devCommand.outputHelp();
});

// ── Subcommand: ss dev detect ──────────────────────────────────────────────

devCommand
  .command('detect')
  .description('Detect project configuration and cache it')
  .option('--reset', 'Invalidate cache before detection')
  .option('--json', 'Output as JSON')
  .option('--verbose', 'Print resolution sources for debugging')
  .action(async (options: { reset?: boolean; json?: boolean; verbose?: boolean }) => {
    if (options.reset && existsSync(DEV_RUN_CACHE)) {
      try {
        const { unlinkSync } = require('fs') as typeof import('fs');
        unlinkSync(DEV_RUN_CACHE);
      } catch { /* ignore */ }
    }

    const apiDir = detectApiDir(PROJECT_ROOT);
    if (!apiDir) {
      const err = `Could not detect a SmartStack API directory at or above ${process.cwd()} (looked for src/*.Api/ with appsettings.json, or appsettings.json at the project root).`;
      if (options.json) {
        process.stdout.write(JSON.stringify({ error: err }) + '\n');
      } else {
        logger.error(err);
        logger.info('Expected: src/*.Api/ or appsettings.json at project root.');
      }
      process.exitCode = 1;
      return;
    }

    const webDir = detectWebDir(PROJECT_ROOT);
    if (!webDir) {
      const err = 'Could not detect web directory.';
      if (options.json) {
        process.stdout.write(JSON.stringify({ error: err }) + '\n');
      } else {
        logger.error(err);
        logger.info('Expected: web/<project>-web/package.json or vite at root.');
      }
      process.exitCode = 1;
      return;
    }

    const hasLocalConfig = existsSync(join(apiDir, 'appsettings.Local.json'));
    const mode = hasLocalConfig ? 'Local' : 'Development';

    const launchSettingsPath = join(apiDir, 'Properties', 'launchSettings.json');
    const launchInfo = existsSync(launchSettingsPath)
      ? pickLaunchProfile(launchSettingsPath, hasLocalConfig)
      : null;

    const apiPort = launchInfo?.port ?? 5142;
    const launchProfile = launchInfo?.profile ?? 'http';
    // A pre-existing project may still carry a dual `https://…;http://…` Local
    // profile. Dev is HTTP-only now, so flag it: the SKILL normalizes it once
    // (transparent `ss dev configure`, same ports) so the backend stops 307-ing.
    const legacyHttpsProfile = launchProfile === 'Local' && profileServesHttps(launchInfo?.applicationUrl);

    const settingsLoad = loadAppSettings(apiDir, hasLocalConfig ? 'Local' : null);
    const conn = extractMergedConnectionString(settingsLoad);
    const corsResolved = extractMergedCorsOrigins(settingsLoad);
    const frontendUrlResolved = extractMergedFrontendUrl(settingsLoad);
    const adminResolved = extractMergedInitialAdmin(settingsLoad);

    const serilogFile = extractMergedSerilogFile(settingsLoad);
    const rawLogPath = serilogFile.value?.path ?? 'logs/smartstack-.log';
    const logDir = isAbsolute(rawLogPath) ? dirname(rawLogPath) : join(apiDir, dirname(rawLogPath));
    const logEnabled = serilogFile.value?.enabled ?? false;

    const connectionString = conn.value || '';
    const connInfo = connectionString
      ? parseConnectionString(connectionString)
      : { server: 'unknown', database: 'unknown', useWindowsAuth: false };

    const webPortInfo = extractWebPort(webDir);
    const viteUrl = extractViteApiUrl(webDir);
    const frontendCmdInfo = getFrontendCmd(webDir);

    const backendCmd = `dotnet run --launch-profile ${launchProfile}`;

    const warnings: string[] = [];

    // Preflight — guarantee FileStorage:BasePath is defined before launch. The
    // platform's LocalFileStorageService fail-fasts on an empty BasePath (the
    // template ships ""), 400-ing the branding + tenant-preferences endpoints at
    // boot. When the existing Local config has no value, seed an absolute dev
    // default into the gitignored appsettings.Local.json + create the dir. (When the
    // whole Local.json is missing, the seed_local_config step writes it WITH
    // FileStorage via `ss dev configure`.)
    const fileStorageResolved = extractMergedFileStorageBasePath(settingsLoad);
    if (!fileStorageResolved.value && hasLocalConfig) {
      const fsBasePath = resolveDevFileStorageBasePath(apiDir);
      try {
        mkdirSync(fsBasePath, { recursive: true });
        const localFsPath = join(apiDir, 'appsettings.Local.json');
        const existingLocalFs = readJsonFile<Record<string, unknown>>(localFsPath) ?? {};
        writeJsonFile(localFsPath, deepMerge(existingLocalFs, { FileStorage: { BasePath: fsBasePath } }));
        warnings.push(
          `FileStorage:BasePath was empty — seeded dev default "${fsBasePath}" into appsettings.Local.json (the platform requires a non-empty BasePath).`
        );
      } catch {
        warnings.push('FileStorage:BasePath is empty and could not be auto-seeded — set it in appsettings.Local.json before boot, or the branding endpoints 400.');
      }
    }

    const corsOrigins = corsResolved.value ?? [];
    const webOrigin = `http://localhost:${webPortInfo.port}`;
    const corsOk = corsOrigins.length === 0 || corsOrigins.some((o) => o === webOrigin || o === '*');
    if (!corsOk && corsOrigins.length > 0) {
      warnings.push(`CORS: expected ${webOrigin} in AllowedOrigins, found [${corsOrigins.join(', ')}]`);
    }

    const expectedApiUrl = `http://localhost:${apiPort}`;
    const viteOk = !viteUrl.url || viteUrl.url.includes(`:${apiPort}`);
    if (!viteOk) {
      warnings.push(`VITE_API_URL: expected ${expectedApiUrl}, got ${viteUrl.url} (${viteUrl.source})`);
    }

    const frontendUrl = frontendUrlResolved.value;
    const frontendUrlOk = !frontendUrl || frontendUrl.includes(`:${webPortInfo.port}`);
    if (!frontendUrlOk) {
      warnings.push(`Authentication.FrontendUrl: expected :${webPortInfo.port}, got ${frontendUrl}`);
    }

    const mismatch = detectModeMismatch(apiPort, launchProfile, viteUrl.url, viteUrl.source);
    if (mismatch) warnings.push(mismatch);

    if (!existsSync(join(webDir, 'node_modules'))) {
      warnings.push(`Frontend dependencies not installed. Run: cd ${webDir} && npm install`);
    }

    const hardcodedVite = detectHardcodedVitePort(webDir);
    if (hardcodedVite && hardcodedVite.port !== webPortInfo.port) {
      warnings.push(
        `package.json scripts.${hardcodedVite.script} hardcodes --port ${hardcodedVite.port}, which overrides VITE_PORT=${webPortInfo.port}. Remove the --port flag so VITE_PORT drives the frontend port.`
      );
    }

    // ── Branch / port-occupancy awareness + config status ──────────────────
    // The branch-specific `occupied` trigger only fires on a non-default branch
    // (parallel worktree), so the main checkout's own running instance is never
    // mistaken for a conflict. A real mode mismatch always triggers. (A missing
    // appsettings.Local.json is handled separately by the auto-seed step, not here.)
    const gitBranch = getGitBranch();
    const defaultBranch = isDefaultBranch(gitBranch);
    const apiBusyPid = getListeningPid(apiPort);
    const webBusyPid = getListeningPid(webPortInfo.port);
    // Our OWN running servers (from a previous `ss dev start`) must not count as a
    // conflict. Cross-check the PID sidecar so `--reset` over a healthy env reuses
    // it (idempotent) instead of triggering a pointless port-change spiral.
    // Read + prune the sidecar (drop records whose captured child no longer listens),
    // then classify with the PORT fallback ENABLED: a just-launched env whose child
    // PID we have not captured yet (or a legacy v1 record) must not be misread as a
    // foreign conflict. status/summary stay strict so a stranger is never "up".
    const sidecar = readPidFile();
    if (pruneDeadSidecar(sidecar)) {
      if (sidecar && (sidecar.backend || sidecar.frontend)) writeJsonFile(DEV_RUN_PIDS, sidecar);
      else { try { unlinkSync(DEV_RUN_PIDS); } catch { /* ignore */ } }
    }
    const occupancy = classifyOccupancy({
      apiPort,
      webPort: webPortInfo.port,
      apiBusyPid,
      webBusyPid,
      pids: toSidecarPids(sidecar),
      allowPortFallback: true,
    });
    const portsOccupied = apiBusyPid !== null || webBusyPid !== null;
    const mismatchInfo = buildMismatchInfo(apiPort, launchProfile, viteUrl.url, viteUrl.source);

    // A missing appsettings.Local.json is NO LONGER a prompt: `ss dev up` auto-seeds it
    // (see local_config_missing below + the orchestrator's seed_local_config step). So
    // config_status only ever reports a real PORT problem now (mismatch / occupied).
    let configStatus: 'ok' | 'mismatch' | 'occupied';
    if (mismatchInfo !== null) configStatus = 'mismatch';
    else if (occupancy.occupiedByOther && !defaultBranch) configStatus = 'occupied';
    else configStatus = 'ok';

    // Scan for free ports ONLY when there is a problem (keep the happy path fast).
    // HTTP-only dev → just two ports (api + web), no https.
    let proposedPorts: { api: number; web: number } | null = null;
    if (configStatus !== 'ok') {
      const webBase = webPortInfo.source ? webPortInfo.port : 6173;
      try {
        const [api, web] = await findFreePorts([apiPort, webBase]);
        proposedPorts = { api, web };
      } catch {
        proposedPorts = null;
      }
    }

    let sqlStatus: 'ok' | 'warning' = 'warning';
    if (connInfo.server !== 'unknown') {
      const sqlHost = connInfo.server.replace(/^\(local\)$/i, 'localhost').replace(/^\.$/i, 'localhost');
      sqlStatus = await testTcpConnection(sqlHost, 1433);
    }

    const sources: Record<string, string> = {
      api_port: launchInfo
        ? `Properties/launchSettings.json:${launchProfile}`
        : 'default (5142)',
      web_port: webPortInfo.source ? `${webPortInfo.source}:VITE_PORT` : 'default (5173)',
      backend_cmd: launchInfo ? `launchSettings.json:${launchProfile}` : 'default',
      frontend_cmd: frontendCmdInfo.source,
      connection_string: conn.source ?? 'absent',
      cors_origins: corsResolved.source ?? 'absent',
      frontend_url: frontendUrlResolved.source ?? 'absent',
      admin: adminResolved.source ?? 'absent',
      vite_api_url: viteUrl.source ?? 'absent',
      logs: serilogFile.source ?? 'default (logs/)',
    };

    const trackedFiles = [
      join(apiDir, 'appsettings.json'),
      join(apiDir, 'appsettings.Local.json'),
      join(apiDir, 'appsettings.Development.json'),
      launchSettingsPath,
      ...['.env.standalone', '.env.development', '.env.local', '.env'].map((f) => join(webDir, f)),
    ];
    const sourceFiles = computeFilesMeta(trackedFiles);

    const result = {
      api_dir: apiDir,
      web_dir: webDir,
      mode,
      launch_profile: launchProfile,
      api_port: apiPort,
      web_port: webPortInfo.port,
      backend_cmd: backendCmd,
      frontend_cmd: frontendCmdInfo.cmd,
      connection_string: connectionString,
      sql_server: connInfo.server,
      sql_database: connInfo.database,
      sql_status: sqlStatus,
      cors_ok: corsOk,
      vite_ok: viteOk,
      frontend_url_ok: frontendUrlOk,
      cors_origins: corsOrigins,
      vite_api_url: viteUrl.url,
      admin_email: adminResolved.value?.email ?? null,
      admin_source: adminResolved.source ?? null,
      log_dir: logDir,
      log_path: rawLogPath,
      log_enabled: logEnabled,
      frontend_deps_ready: existsSync(join(webDir, 'node_modules')),
      api_probe_endpoint: '/api/config/features',
      sources,
      source_files: sourceFiles,
      cache_max_age_days: CACHE_MAX_AGE_DAYS,
      cached: false,
      warnings,
      git_branch: gitBranch,
      is_default_branch: defaultBranch,
      ports_occupied: portsOccupied,
      ports_occupied_by_self: occupancy.occupiedBySelf,
      legacy_https_profile: legacyHttpsProfile,
      api_busy_pid: apiBusyPid,
      web_busy_pid: webBusyPid,
      config_status: configStatus,
      // Drives the orchestrator's seed_local_config step: when true, `ss dev up`
      // auto-creates appsettings.Local.json (every branch) before launching in Local mode.
      local_config_missing: !hasLocalConfig,
      mismatch: mismatchInfo,
      proposed_ports: proposedPorts,
      cached_at: new Date().toISOString(),
    };

    writeJsonFile(DEV_RUN_CACHE, result);

    if (options.json) {
      process.stdout.write(JSON.stringify(result) + '\n');
      return;
    }

    console.log();
    logger.header('SmartStack Dev Detect');
    console.log(`  ${chalk.cyan('API dir:   ')} ${apiDir}`);
    console.log(`  ${chalk.cyan('Web dir:   ')} ${webDir}`);
    console.log(`  ${chalk.cyan('Mode:      ')} ${mode}  (profile: ${launchProfile})`);
    console.log(`  ${chalk.cyan('API port:  ')} ${apiPort}  ${chalk.gray('← ' + sources.api_port)}`);
    console.log(`  ${chalk.cyan('Web port:  ')} ${webPortInfo.port}  ${chalk.gray('← ' + sources.web_port)}`);
    console.log(`  ${chalk.cyan('Database:  ')} ${connInfo.database}  ${chalk.gray('← ' + sources.connection_string)}`);
    console.log(`  ${chalk.cyan('Admin:     ')} ${adminResolved.value?.email ?? chalk.red('absent')}  ${chalk.gray('← ' + sources.admin)}`);
    console.log(`  ${chalk.cyan('SQL:       ')} ${connInfo.server} [${sqlStatus === 'ok' ? chalk.green('ok') : chalk.yellow('warning')}]`);
    console.log(`  ${chalk.cyan('CORS:      ')} ${corsOk ? chalk.green('ok') : chalk.yellow('warn')}`);
    console.log(`  ${chalk.cyan('VITE URL:  ')} ${viteOk ? chalk.green('ok') : chalk.yellow('warn')}`);
    console.log(`  ${chalk.cyan('Logs:      ')} ${logEnabled ? logDir : chalk.yellow(logDir + ' (file sink off — console only)')}  ${chalk.gray('← ' + sources.logs)}`);

    if (options.verbose) {
      console.log();
      logger.header('Sources');
      for (const [k, v] of Object.entries(sources)) {
        console.log(`  ${chalk.cyan(k.padEnd(20))} ${chalk.gray('← ' + v)}`);
      }
    }

    if (warnings.length > 0) {
      console.log();
      for (const w of warnings) logger.warning(w);
    }

    if (configStatus !== 'ok' && proposedPorts) {
      console.log();
      logger.warning(`config_status: ${configStatus}${gitBranch ? ` (branch ${gitBranch})` : ''}`);
      logger.info(
        `Proposed free ports → api ${proposedPorts.api}, web ${proposedPorts.web}  (run /dev-start to auto-configure)`
      );
    }

    console.log();
    logger.success(`Config cached to ${DEV_RUN_CACHE}`);
  });

// ── Subcommand: ss dev configure ───────────────────────────────────────────

devCommand
  .command('configure')
  .description('Write a coherent per-branch dev config (appsettings.Local.json + launchSettings Local profile + frontend env)')
  .option('--spec <json>', 'Configuration spec as JSON')
  .option('--auto-port', 'Fill any missing/zero ports by scanning for free ones')
  .option('--dry-run', 'Compute the files but do not write them')
  .option('--path <path>', 'Path to API folder (auto-detected from cache otherwise)')
  .option('--web <path>', 'Path to web folder (auto-detected from cache otherwise)')
  .option('--json', 'Output as JSON')
  .action(
    async (options: {
      spec?: string;
      autoPort?: boolean;
      dryRun?: boolean;
      path?: string;
      web?: string;
      json?: boolean;
    }) => {
      const fail = (msg: string, code: number): void => {
        if (options.json) process.stdout.write(JSON.stringify({ success: false, error: msg }) + '\n');
        else logger.error(msg);
        process.exitCode = code;
      };

      if (!options.spec) {
        return fail('Missing --spec. Provide the configuration as JSON.', 2);
      }

      // 1) Parse raw JSON (so we can auto-fill ports before validation)
      let raw: Record<string, unknown>;
      try {
        const parsed = JSON.parse(options.spec);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          return fail('Spec must be a JSON object.', 2);
        }
        raw = parsed as Record<string, unknown>;
      } catch (e) {
        return fail(`Invalid JSON spec: ${(e as Error).message}`, 2);
      }

      // 2) --auto-port: fill missing/zero ports with free ones
      // HTTP-only dev → fill just the two ports that matter (api + web).
      if (options.autoPort) {
        const needApi = !raw.apiPort;
        const needWeb = !raw.webPort;
        if (needApi || needWeb) {
          const apiBase = (raw.apiPort as number) || 5142;
          const webBase = (raw.webPort as number) || 6173;
          try {
            const [api, web] = await findFreePorts([apiBase, webBase]);
            if (needApi) raw.apiPort = api;
            if (needWeb) raw.webPort = web;
          } catch (e) {
            return fail(`Could not find free ports: ${(e as Error).message}`, 1);
          }
        }
      }

      // 3) Validate
      const validated = validateConfigureSpecObject(raw);
      if (!validated.ok) {
        return fail(validated.errors.join('; '), 2);
      }
      const spec = validated.spec;

      // 4) Resolve directories: spec → flags → cache
      const cache = readJsonFile<{ api_dir?: string; web_dir?: string }>(DEV_RUN_CACHE);
      const apiDir = spec.apiDir || options.path || cache?.api_dir;
      const webDir = spec.webDir || options.web || cache?.web_dir;
      if (!apiDir || !existsSync(apiDir)) {
        return fail('API directory not found. Pass apiDir in the spec, --path, or run ss dev detect first.', 1);
      }
      if (!webDir || !existsSync(webDir)) {
        return fail('Web directory not found. Pass webDir in the spec, --web, or run ss dev detect first.', 1);
      }

      // 5) Compute the files IN MEMORY (atomic: write only if all compute)
      const writtenFiles: string[] = [];
      let jwtGenerated = false;
      let appsettingsLocalCreated = false;
      let gitignored: string[] = [];
      let gitignorePathOut: string | null = null;
      let finalConnString: string | null = null;
      try {
        // (A) appsettings.Local.json — deep-merge patch onto the existing override
        const localPath = join(apiDir, 'appsettings.Local.json');
        appsettingsLocalCreated = !existsSync(localPath);
        const existingLocal = readJsonFile<Record<string, unknown>>(localPath) ?? {};

        // Ports-only reconfigure (no SQL block AND no pre-built connectionString) needs an
        // existing connection string to preserve — refuse a first-setup with nothing to
        // write. A spec.connectionString (the auto-seed path) writes a fresh Local directly.
        if (!hasSqlSpec(spec) && !spec.connectionString) {
          const existingConn = (existingLocal.ConnectionStrings as Record<string, unknown> | undefined)
            ?.DefaultConnection;
          if (typeof existingConn !== 'string' || existingConn.length === 0) {
            return fail(
              'Ports-only reconfigure needs an existing appsettings.Local.json connection string to preserve. ' +
                'For a first setup, provide authMode + sqlServer + sqlDatabase (+ sqlLogin/sqlPassword for sql auth), or a pre-built connectionString.',
              2
            );
          }
        }

        const existingJwt =
          existingLocal.Jwt && typeof existingLocal.Jwt === 'object'
            ? (existingLocal.Jwt as Record<string, unknown>)
            : {};
        const existingSecret = typeof existingJwt.Secret === 'string' ? existingJwt.Secret : null;

        // Ensure a non-empty FileStorage:BasePath. Seed an absolute dev default ONLY
        // when the existing Local value is empty (a custom path is preserved). The
        // platform fail-fasts on an empty BasePath → 400 on the branding endpoints.
        const existingFs = (existingLocal.FileStorage as { BasePath?: unknown } | undefined)?.BasePath;
        const hasFsBasePath = typeof existingFs === 'string' && existingFs.trim().length > 0;
        if (!hasFsBasePath && !spec.fileStorageBasePath) {
          spec.fileStorageBasePath = resolveDevFileStorageBasePath(apiDir);
          try { mkdirSync(spec.fileStorageBasePath, { recursive: true }); } catch { /* best-effort */ }
        }

        const { patch, jwtGenerated: gen } = buildAppsettingsLocalPatch(spec, existingSecret);
        jwtGenerated = gen;

        // (B) launchSettings.json — set/replace the Local profile (HTTP-only), preserve the rest.
        const launchPath = join(apiDir, 'Properties', 'launchSettings.json');
        const existingLaunch = readJsonFile<Record<string, unknown>>(launchPath);

        // Resolve the OUTGOING ports (from the config we are about to overwrite) so a
        // port change is swept through EVERY url in appsettings.Local.json — including
        // app-specific keys configure does not know by name (Authentication.FrontendUrl,
        // Email.BaseUrl, OAuth callbacks…). Without this the old port lingers and the
        // agent is forced to hand-edit (the PickEBike 6173→4100 trace).
        const existingLocalAppUrl =
          ((existingLaunch?.profiles as Record<string, { applicationUrl?: string }> | undefined)?.Local
            ?.applicationUrl) ?? null;
        const oldApiPort = extractApiPort(existingLocalAppUrl);
        const oldWebPort = extractWebPort(webDir).port;
        const portMappings = [
          { from: oldApiPort ?? spec.apiPort, to: spec.apiPort },
          { from: oldWebPort, to: spec.webPort },
        ];

        let mergedLocal = deepMerge(existingLocal, patch);
        mergedLocal = rewritePortsDeep(mergedLocal, portMappings);

        // The effective connection string after the merge (used by the ensure-DB probe).
        const mergedConn = (mergedLocal.ConnectionStrings as Record<string, unknown> | undefined)
          ?.DefaultConnection;
        finalConnString = typeof mergedConn === 'string' && mergedConn.length > 0 ? mergedConn : null;

        const mergedLaunch = applyLocalProfile(existingLaunch, spec.apiPort);

        // (C) frontend env — target the file detect reads VITE_API_URL from, else .env.local
        const viteSrc = extractViteApiUrl(webDir);
        const envFileName = viteSrc.source ?? '.env.local';
        const envPath = join(webDir, envFileName);
        const existingEnv = existsSync(envPath) ? readFileSync(envPath, 'utf-8') : '';
        const newEnv = patchEnvContent(existingEnv, spec.apiPort, spec.webPort);

        // (D) ensure appsettings.Local.json is gitignored — it holds the SQL password + JWT.
        // Target the git root's .gitignore (filename pattern matches at any depth); fall back to apiDir.
        let gitRoot: string | null = null;
        try {
          gitRoot =
            execSync('git rev-parse --show-toplevel', {
              cwd: apiDir,
              encoding: 'utf-8',
              timeout: 2000,
              stdio: ['ignore', 'pipe', 'ignore'],
            }).trim() || null;
        } catch {
          gitRoot = null;
        }
        const gitignorePath = join(gitRoot ?? apiDir, '.gitignore');
        const existingGitignore = existsSync(gitignorePath) ? readFileSync(gitignorePath, 'utf-8') : '';
        const gi = ensureGitignoreLines(existingGitignore, ['appsettings.Local.json']);

        // 6) Write (unless --dry-run)
        if (!options.dryRun) {
          writeJsonFile(localPath, mergedLocal);
          writeJsonFile(launchPath, mergedLaunch);
          const envDir = dirname(envPath);
          if (!existsSync(envDir)) mkdirSync(envDir, { recursive: true });
          writeFileSync(envPath, newEnv, 'utf-8');
          if (gi.added.length > 0) writeFileSync(gitignorePath, gi.content, 'utf-8');
        }
        writtenFiles.push(localPath, launchPath, envPath);
        if (gi.added.length > 0) {
          gitignored = gi.added;
          gitignorePathOut = gitignorePath;
          writtenFiles.push(gitignorePath);
        }
      } catch (e) {
        return fail(`Failed to write configuration: ${(e as Error).message}`, 1);
      }

      // 7) Ensure the target database exists (best-effort, opt-in via spec.ensureDatabase).
      // NEVER fatal: a missing sqlcmd or an unreachable server becomes a warning — the
      // app's the app applies its EF Core migrations at startup and creates the database/schema on first boot.
      const warnings: string[] = [];
      let sqlProbe: SqlProbeResult | null = null;
      if (spec.ensureDatabase && !options.dryRun && finalConnString) {
        sqlProbe = ensureDatabaseExists(finalConnString);
        if (sqlProbe.created) {
          // success — surfaced below
        } else if (!sqlProbe.available) {
          warnings.push(`SQL: ${sqlProbe.error ?? 'sqlcmd not available — skipped the database check.'}`);
        } else if (!sqlProbe.reachable) {
          warnings.push(
            `SQL: server unreachable — ${sqlProbe.error ?? 'no answer'}. The database will be created at backend boot if the server comes up.`
          );
        } else if (!sqlProbe.ok) {
          warnings.push(`SQL: ${sqlProbe.error ?? 'could not verify the database.'}`);
        }
      }

      const portsOnly = !hasSqlSpec(spec);
      const summary = {
        success: true,
        written_files: writtenFiles,
        ports_only: portsOnly,
        api_port: spec.apiPort,
        web_port: spec.webPort,
        auth_mode: spec.authMode ?? 'preserved',
        sql_server: spec.sqlServer ?? null,
        sql_database: spec.sqlDatabase ?? null,
        jwt_generated: jwtGenerated,
        appsettings_local_created: appsettingsLocalCreated,
        initial_admin_seeded: Boolean(spec.initialAdminEmail && spec.initialAdminPassword),
        admin_email: spec.initialAdminEmail ?? null,
        sql_probe: sqlProbe
          ? {
              attempted: true,
              available: sqlProbe.available,
              reachable: sqlProbe.reachable,
              existed: sqlProbe.existed,
              created: sqlProbe.created,
              error: sqlProbe.error,
            }
          : null,
        warnings,
        gitignored,
        gitignore_path: gitignorePathOut,
        launch_profile: 'Local',
        aspnetcore_environment: 'Local',
        dry_run: !!options.dryRun,
      };

      if (options.json) {
        process.stdout.write(JSON.stringify(summary) + '\n');
        return;
      }

      console.log();
      logger.success(`${options.dryRun ? '[dry-run] Would write' : 'Wrote'} dev configuration (Local profile)`);
      console.log(`  ${chalk.cyan('API port:  ')} ${spec.apiPort}  (http-only)`);
      console.log(`  ${chalk.cyan('Web port:  ')} ${spec.webPort}`);
      console.log(
        portsOnly
          ? `  ${chalk.cyan('SQL:       ')} ${chalk.gray('preserved (ports-only reconfigure)')}`
          : `  ${chalk.cyan('SQL:       ')} ${spec.sqlServer} / ${spec.sqlDatabase}  (${spec.authMode === 'windows' ? 'Windows auth' : 'SQL auth'})`
      );
      console.log(`  ${chalk.cyan('JWT:       ')} ${jwtGenerated ? chalk.green('generated') : 'preserved'}`);
      if (summary.initial_admin_seeded) {
        console.log(`  ${chalk.cyan('Admin:     ')} ${summary.admin_email} ${chalk.green('(seeded)')}`);
      }
      if (sqlProbe) {
        const dbState = sqlProbe.created
          ? chalk.green(`created (${spec.sqlDatabase})`)
          : sqlProbe.existed
          ? chalk.gray(`exists (${spec.sqlDatabase})`)
          : !sqlProbe.available
          ? chalk.yellow('skipped — sqlcmd not found')
          : chalk.yellow('unreachable — will be created at boot');
        console.log(`  ${chalk.cyan('Database:  ')} ${dbState}`);
      }
      console.log(
        `  ${chalk.cyan('Local cfg: ')} appsettings.Local.json ${appsettingsLocalCreated ? chalk.green('created') : 'updated'}${gitignored.length ? chalk.green(' + gitignored (secrets)') : chalk.gray(' (already gitignored)')}`
      );
      for (const f of writtenFiles) console.log(`  ${chalk.gray((options.dryRun ? '· ' : '✓ ') + f)}`);
      for (const w of warnings) console.log(`  ${chalk.yellow('⚠ ' + w)}`);
    }
  );

// ── Subcommand: ss dev creds ───────────────────────────────────────────────

devCommand
  .command('creds')
  .description('Read admin credentials from appsettings.{Env}.json (SmartStack.app v3.46+)')
  .option('--env <env>', 'Environment to read (e.g. Development, Local)')
  .option('--path <path>', 'Path to API folder (auto-detected from cache otherwise)')
  .option('--json', 'Output as JSON')
  .action(async (options: { env?: string; path?: string; json?: boolean }) => {
    let apiDir: string | undefined = options.path;
    if (!apiDir) {
      const cache = readJsonFile<{ api_dir?: string }>(DEV_RUN_CACHE);
      if (cache?.api_dir) apiDir = cache.api_dir;
    }

    if (!apiDir || !existsSync(apiDir)) {
      const err = 'API directory not found. Run ss dev detect first.';
      if (options.json) {
        process.stdout.write(JSON.stringify({ error: err }) + '\n');
      } else {
        logger.error(err);
      }
      process.exitCode = 1;
      return;
    }

    // Resolve the admin from the DEEP-MERGED effective config (base +
    // appsettings.{Mode}.json, override wins at the leaf) — exactly what the
    // running backend seeds. This is the SAME resolver `detect` uses, so the
    // password matches the email's source (the old single-file candidate-pick
    // could land on base appsettings.json and surface a hash instead of the real
    // plaintext from appsettings.Local.json). `--env` forces a mode; otherwise
    // use the cached mode (from detect), falling back to Local when present.
    const modeCache = readJsonFile<{ mode?: string }>(DEV_RUN_CACHE);
    const mode =
      options.env ??
      modeCache?.mode ??
      (existsSync(join(apiDir, 'appsettings.Local.json')) ? 'Local' : 'Development');

    const load = loadAppSettings(apiDir, mode);
    const resolved = extractMergedInitialAdmin(load);

    if (!resolved.value) {
      if (options.json) {
        process.stdout.write(JSON.stringify({
          error: 'Security.InitialAdmin (Email+Password) not found in the merged appsettings.',
          hint: 'SmartStack.app v3.46+ required. Add Security.InitialAdmin to appsettings.{Mode}.json.',
        }) + '\n');
      } else {
        console.log();
        logger.error(`Section Security.InitialAdmin (Email+Password) absent from the merged config in ${apiDir}.`);
        console.log();
        console.log(chalk.yellow('SmartStack.app v3.46+ requis. Ajoute la section suivante dans appsettings.{Mode}.json :'));
        console.log();
        console.log(chalk.gray('  "Security": {'));
        console.log(chalk.gray('    "InitialAdmin": {'));
        console.log(chalk.gray(`      "Email": "local.admin@smartstack.local",`));
        console.log(chalk.gray(`      "Password": "<your-dev-password>"`));
        console.log(chalk.gray('    }'));
        console.log(chalk.gray('  }'));
        console.log();
      }
      process.exitCode = 1;
      return;
    }

    const creds = resolved.value;
    const source = resolved.source ?? 'appsettings.json';
    const env = mode;

    writeJsonFile(DEV_SESSION, {
      version: 2,
      status: 'ready',
      admin_email: creds.email,
      admin_password: creds.password,
      source,
      env,
      ready_at: new Date().toISOString(),
    });

    if (options.json) {
      process.stdout.write(JSON.stringify({
        admin_email: creds.email,
        admin_password: creds.password,
        source,
        env,
      }) + '\n');
      return;
    }

    console.log();
    logger.success(`Credentials read from ${chalk.cyan(source)}`);
    console.log(`  ${chalk.cyan('Email:    ')} ${creds.email}`);
    console.log(`  ${chalk.cyan('Password: ')} ${chalk.yellow(creds.password)}`);
    console.log(`  ${chalk.cyan('Env:      ')} ${env}`);
    logger.success(`Session saved to ${DEV_SESSION}`);
  });

// ── Subcommand: ss dev status ──────────────────────────────────────────────

devCommand
  .command('status')
  .description('Check if backend and frontend are running, and whether the cache is fresh')
  .option('--json', 'Output as JSON (default)')
  .action(async (_options: { json?: boolean }) => {
    interface CachedRun {
      api_port?: number;
      web_port?: number;
      cached_at?: string;
      source_files?: FileMeta[];
      cache_max_age_days?: number;
    }
    const cache = readJsonFile<CachedRun>(DEV_RUN_CACHE);

    const cacheStaleResult = computeCacheStale(cache);

    if (!cache?.api_port || !cache?.web_port) {
      const result = {
        backend_running: false,
        frontend_running: false,
        api_pid: null,
        web_pid: null,
        cache_stale: true,
        cache_age_days: null,
        stale_reason: 'missing',
      };
      process.stdout.write(JSON.stringify(result) + '\n');
      return;
    }

    const apiPid = getListeningPid(cache.api_port);
    const webPid = getListeningPid(cache.web_port);

    // "running" means OUR child is the live listener — NOT merely that the port is
    // busy. Strict identity (no port fallback) so a foreign server squatting our
    // port is never reported up (which would otherwise fire the idempotent shortcut
    // onto it and paint it ✓ up in the cartouche).
    const occ = classifyOccupancy({
      apiPort: cache.api_port,
      webPort: cache.web_port,
      apiBusyPid: apiPid,
      webBusyPid: webPid,
      pids: toSidecarPids(readPidFile()),
    });

    const result = {
      backend_running: occ.apiBusyBySelf,
      frontend_running: occ.webBusyBySelf,
      api_pid: apiPid,
      web_pid: webPid,
      cache_stale: cacheStaleResult.stale,
      cache_age_days: cacheStaleResult.ageDays,
      stale_reason: cacheStaleResult.reason,
    };
    process.stdout.write(JSON.stringify(result) + '\n');
  });

interface CacheStaleResult {
  stale: boolean;
  ageDays: number | null;
  reason: string | null;
}

function computeCacheStale(cache: {
  cached_at?: string;
  source_files?: FileMeta[];
  cache_max_age_days?: number;
} | null): CacheStaleResult {
  if (!cache) return { stale: true, ageDays: null, reason: 'missing' };
  if (!cache.cached_at) return { stale: true, ageDays: null, reason: 'missing-timestamp' };

  const cachedAt = new Date(cache.cached_at).getTime();
  if (isNaN(cachedAt)) return { stale: true, ageDays: null, reason: 'invalid-timestamp' };

  const ageMs = Date.now() - cachedAt;
  const ageDays = ageMs / (1000 * 60 * 60 * 24);
  const maxAge = cache.cache_max_age_days ?? CACHE_MAX_AGE_DAYS;
  if (ageDays > maxAge) {
    return { stale: true, ageDays, reason: `expired (${ageDays.toFixed(1)}d > ${maxAge}d)` };
  }

  if (cache.source_files && cache.source_files.length > 0) {
    const current = computeFilesMeta(cache.source_files.map((m) => m.path));
    const changed = diffFilesMeta(cache.source_files, current);
    if (changed.length > 0) {
      const names = changed.map((p) => basename(p)).join(', ');
      return { stale: true, ageDays, reason: `files changed: ${names}` };
    }
  }

  return { stale: false, ageDays, reason: null };
}

// ── Subcommand: ss dev healthcheck ─────────────────────────────────────────

devCommand
  .command('healthcheck')
  .description('Verify backend reachability + admin credentials match by attempting a login')
  .option('--json', 'Output as JSON')
  .action(async (options: { json?: boolean }) => {
    const cache = readJsonFile<{ api_port?: number }>(DEV_RUN_CACHE);
    const session = readJsonFile<{ admin_email?: string; admin_password?: string }>(DEV_SESSION);

    if (!cache?.api_port) {
      const err = 'No cache found. Run ss dev detect first.';
      if (options.json) process.stdout.write(JSON.stringify({ ok: false, error: err }) + '\n');
      else logger.error(err);
      process.exitCode = 1;
      return;
    }

    if (!session?.admin_email || !session?.admin_password) {
      const err = 'No session found. Run ss dev creds first.';
      if (options.json) process.stdout.write(JSON.stringify({ ok: false, error: err }) + '\n');
      else logger.error(err);
      process.exitCode = 1;
      return;
    }

    const result = await attemptLogin(cache.api_port, session.admin_email, session.admin_password);

    const summary = {
      ok: result.ok,
      status: result.status,
      endpoint: `http://localhost:${cache.api_port}/api/auth/login`,
      email: session.admin_email,
      error: result.error ?? null,
      body: result.bodySnippet || null,
      hint: result.status === 401
        ? 'Credentials in appsettings do not match the seeded admin in the database. Re-run with --reset to clear cache and re-seed.'
        : result.status === 400
        ? 'Login rejected (400). Admin user may not be seeded yet, or request payload is invalid. Check backend logs.'
        : result.status === 403
        ? 'Admin account may be disabled or locked (403).'
        : result.status === 500
        ? 'Internal server error (500). Check backend logs.'
        : result.status === 503
        ? 'Service unavailable (503). Backend still initializing or license halted.'
        : result.status === 0
        ? 'Backend unreachable or login endpoint missing.'
        : result.status >= 400
        ? `Unexpected HTTP ${result.status}. Check backend logs.`
        : null,
    };

    if (options.json) {
      process.stdout.write(JSON.stringify(summary) + '\n');
    } else {
      console.log();
      if (result.ok) {
        logger.success(`Auth login OK (HTTP ${result.status}) — credentials match the backend`);
      } else if (result.status === 401) {
        logger.error('Auth login FAILED (HTTP 401) — credentials do not match the backend DB');
        if (summary.hint) logger.info(summary.hint);
      } else {
        logger.error(`Auth login FAILED — ${result.error || `HTTP ${result.status}`}`);
        if (summary.hint) logger.info(summary.hint);
      }
    }

    if (!result.ok) process.exitCode = 1;
  });

// ── Subcommand: ss dev wait ────────────────────────────────────────────────

/**
 * GET the API readiness endpoint; resolves the HTTP status (0 on connection
 * error / timeout). Sends a User-Agent (the API middleware rejects requests
 * without one). 127.0.0.1, not localhost, to avoid IPv6 ambiguity on Windows.
 */
function probeApi(apiPort: number, path: string, timeoutMs: number): Promise<number> {
  return new Promise((resolve) => {
    const req = http.request(
      {
        host: '127.0.0.1',
        port: apiPort,
        path,
        method: 'GET',
        headers: { 'User-Agent': DEV_PROBE_USER_AGENT },
        timeout: timeoutMs,
      },
      (res) => {
        res.resume(); // drain so the socket frees
        resolve(res.statusCode || 0);
      }
    );
    req.on('error', () => resolve(0));
    req.on('timeout', () => {
      req.destroy();
      resolve(0);
    });
    req.end();
  });
}

devCommand
  .command('wait')
  .description('Poll the API readiness endpoint until it responds (or timeout) — truthful, no curl')
  .option('--timeout <ms>', 'Total wait budget in ms', '60000')
  .option('--interval <ms>', 'Initial delay between probes in ms (grows ×1.5)', '250')
  .option('--max-interval <ms>', 'Cap for the backoff delay in ms', '2000')
  .option('--json', 'Output as JSON')
  .action(async (options: { timeout?: string; interval?: string; maxInterval?: string; json?: boolean }) => {
    const cache = readJsonFile<{ api_port?: number; api_probe_endpoint?: string }>(DEV_RUN_CACHE);
    if (!cache?.api_port) {
      const err = 'No cache found. Run ss dev detect first.';
      if (options.json) process.stdout.write(JSON.stringify({ ready: false, status: 0, error: err }) + '\n');
      else logger.error(err);
      process.exitCode = 1;
      return;
    }

    const endpoint = cache.api_probe_endpoint || '/api/config/features';
    const totalMs = Math.max(1000, parseInt(options.timeout || '60000', 10) || 60000);
    // P3: probe immediately, then back off ×1.5 from a short initial interval up to a
    // cap — a warm bind is caught in ~0.25-1s instead of the old flat 2s, while a cold
    // boot still gets the full budget without hammering.
    const initialMs = Math.max(50, parseInt(options.interval || '250', 10) || 250);
    const capMs = Math.max(initialMs, parseInt(options.maxInterval || '2000', 10) || 2000);
    const start = Date.now();
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    let attempts = 0;
    let status = 0;
    let intervalMs = initialMs;
    while (Date.now() - start < totalMs) {
      attempts++;
      status = await probeApi(cache.api_port, endpoint, Math.min(5000, intervalMs + 3000));
      if (isServingStatus(status)) break;
      if (Date.now() - start + intervalMs >= totalMs) break;
      await sleep(intervalMs);
      intervalMs = nextWaitInterval(intervalMs, capMs);
    }

    const ready = isServingStatus(status);
    const elapsedMs = Date.now() - start;
    const result = {
      ready,
      status,
      attempts,
      elapsed_ms: elapsedMs,
      endpoint: `http://127.0.0.1:${cache.api_port}${endpoint}`,
    };

    if (options.json) {
      process.stdout.write(JSON.stringify(result) + '\n');
    } else if (ready) {
      logger.success(`API ready (HTTP ${status}) after ${(elapsedMs / 1000).toFixed(1)}s`);
    } else {
      logger.error(`API did not respond within ${(totalMs / 1000).toFixed(0)}s (${attempts} probes)`);
    }

    if (!ready) process.exitCode = 1;
  });

// ── Subcommand: ss dev summary ─────────────────────────────────────────────

devCommand
  .command('summary')
  .description('Print the dev cartouche — URLs + database + login + per-side status. ALWAYS renders.')
  .option('--restarted', 'Title the panel as a restart')
  .option('--no-probe', 'Skip the login health probe')
  .option('--json', 'Output the summary payload as JSON')
  .action(async (options: { restarted?: boolean; probe?: boolean; json?: boolean }) => {
    interface CacheShape {
      api_port?: number;
      web_port?: number;
      api_dir?: string;
      mode?: string;
      launch_profile?: string;
      sql_server?: string;
      sql_database?: string;
      admin_email?: string;
      admin_source?: string;
      log_dir?: string;
      log_enabled?: boolean;
      warnings?: string[];
    }
    const cache = readJsonFile<CacheShape>(DEV_RUN_CACHE);
    if (!cache?.api_port || !cache?.web_port) {
      const err = 'No cache found. Run ss dev detect (or /dev-start) first.';
      if (options.json) process.stdout.write(JSON.stringify({ ok: false, error: err }) + '\n');
      else logger.error(err);
      process.exitCode = 1;
      return;
    }

    // Live per-side check — OUR child must be the listener (strict identity), so a
    // foreign process squatting our port is rendered DOWN, never ✓ up. Port-bound,
    // no curl (immune to the 000000 probe bug).
    const apiPid = getListeningPid(cache.api_port);
    const webPid = getListeningPid(cache.web_port);
    const occ = classifyOccupancy({
      apiPort: cache.api_port,
      webPort: cache.web_port,
      apiBusyPid: apiPid,
      webBusyPid: webPid,
      pids: toSidecarPids(readPidFile()),
    });
    const backendRunning = occ.apiBusyBySelf;
    const frontendRunning = occ.webBusyBySelf;

    // Resolve admin from the merged effective config (real plaintext from Local),
    // falling back to the cached email + session password if the API dir is gone.
    let adminEmail: string | null = cache.admin_email ?? null;
    let adminPassword: string | null = null;
    let adminSource: string | null = cache.admin_source ?? null;
    if (cache.api_dir && existsSync(cache.api_dir)) {
      const mode =
        cache.mode ?? (existsSync(join(cache.api_dir, 'appsettings.Local.json')) ? 'Local' : 'Development');
      const resolved = extractMergedInitialAdmin(loadAppSettings(cache.api_dir, mode));
      if (resolved.value) {
        adminEmail = resolved.value.email;
        adminPassword = resolved.value.password;
        adminSource = resolved.source ?? adminSource;
      }
    }
    if (!adminPassword) {
      const session = readJsonFile<{ admin_email?: string; admin_password?: string }>(DEV_SESSION);
      if (session?.admin_password) {
        adminPassword = session.admin_password;
        adminEmail = adminEmail ?? session.admin_email ?? null;
      }
    }

    // Login health probe — only meaningful when the backend is up.
    let health: { probed: boolean; ok: boolean; status: number; hint: string | null } = {
      probed: false,
      ok: false,
      status: 0,
      hint: null,
    };
    if (options.probe !== false && backendRunning && adminEmail && adminPassword) {
      const login = await attemptLogin(cache.api_port, adminEmail, adminPassword);
      health = {
        probed: true,
        ok: login.ok,
        status: login.status,
        hint: login.ok
          ? null
          : login.status === 401
          ? 'credentials do not match the seeded admin — try /dev-start --reset'
          : login.status === 0
          ? 'backend unreachable on the login endpoint'
          : `unexpected HTTP ${login.status} — check backend logs`,
      };
    }

    const input: DevSummaryInput = {
      apiPort: cache.api_port,
      webPort: cache.web_port,
      launchProfile: cache.launch_profile ?? null,
      backendRunning,
      frontendRunning,
      dbServer: cache.sql_server ?? null,
      dbName: cache.sql_database ?? null,
      adminEmail,
      adminPassword,
      adminSource,
      logDir: cache.log_dir ?? null,
      logEnabled: cache.log_enabled ?? false,
      launcherLogDir: LAUNCHER_LOG_DIR,
      logsRel: cache.log_dir ? toProjectRel(cache.log_dir) : null,
      consoleRel: toProjectRel(LAUNCHER_LOG_DIR),
      health,
      warnings: cache.warnings ?? [],
    };
    const summary = buildSummary(input);

    if (options.json) {
      process.stdout.write(JSON.stringify(summary) + '\n');
    } else {
      console.log();
      console.log(renderSummary(summary, { restarted: options.restarted }));
      console.log();
    }

    // The cartouche rendering IS the success signal. A health hiccup (e.g. a
    // transient login-probe failure) must NOT fail the command — otherwise the
    // calling agent reads a non-zero exit as "the env is broken" and loops with
    // needless restarts (the PickEBike trace). Exit non-zero ONLY when nothing is
    // up at all; `summary.ok` (which folds in health) stays in the JSON for info.
    process.exitCode = backendRunning || frontendRunning ? 0 : 1;
  });

// ── Subcommand: ss dev stop ────────────────────────────────────────────────

devCommand
  .command('stop')
  .description('Stop backend and/or frontend processes')
  .option('--backend-only', 'Stop only the backend')
  .option('--frontend-only', 'Stop only the frontend')
  .option('--json', 'Output as JSON')
  .action(async (options: { backendOnly?: boolean; frontendOnly?: boolean; json?: boolean }) => {
    const cache = readJsonFile<{ api_port?: number; web_port?: number }>(DEV_RUN_CACHE);

    if (!cache?.api_port || !cache?.web_port) {
      const result = { stopped: [] as string[], skipped: [] as string[], error: 'No cache found. Run ss dev detect first.' };
      if (options.json) process.stdout.write(JSON.stringify(result) + '\n');
      else logger.error('No dev cache found. Run ss dev detect first.');
      process.exitCode = 1;
      return;
    }

    const stopped: string[] = [];
    const skipped: string[] = [];

    const killPid = (pid: number): boolean => {
      try {
        const r = spawnSync('taskkill', ['/F', '/T', '/PID', String(pid)], {
          encoding: 'utf-8',
          timeout: 5000,
          stdio: 'pipe',
        });
        return r.status === 0;
      } catch {
        return false;
      }
    };

    const stopProcess = (port: number, label: string, force: boolean): void => {
      if (!force) { skipped.push(label); return; }
      const pid = getListeningPid(port);
      if (pid === null) { skipped.push(label); return; }
      killPid(pid);
      stopped.push(label);
    };

    const stopBackend = !options.frontendOnly;
    const stopFrontend = !options.backendOnly;

    stopProcess(cache.api_port, 'backend', stopBackend);
    stopProcess(cache.web_port, 'frontend', stopFrontend);

    // PID-file fallback: kill any recorded launcher PID for an in-scope side that the
    // port pass missed — e.g. a server that crashed at boot and never bound its port
    // (invisible to getListeningPid), or the lingering `cmd.exe` wrapper. Best-effort,
    // then forget the record so the next start writes a fresh one.
    const pidFile = readPidFile();
    if (pidFile) {
      let mutated = false;
      const reapSide = (side: 'backend' | 'frontend', force: boolean): void => {
        const rec = pidFile[side];
        if (!rec || !force) return;
        // Tree-kill the wrapper: `/T` takes its dotnet/node child too — including one
        // that crashed before binding and is invisible to the port pass.
        let killed = killPid(rec.wrapperPid);
        // Verify the port actually freed; if a child still holds it, kill it directly.
        if (rec.listenPid !== null && getListeningPid(rec.port, true) !== null) {
          if (killPid(rec.listenPid)) killed = true;
        }
        delete pidFile[side];
        mutated = true;
        if (killed) {
          // The port pass thought this side was already gone — promote it to stopped.
          const si = skipped.indexOf(side);
          if (si !== -1) skipped.splice(si, 1);
          if (!stopped.includes(side)) stopped.push(side);
        }
      };
      reapSide('backend', stopBackend);
      reapSide('frontend', stopFrontend);
      if (mutated) {
        if (!pidFile.backend && !pidFile.frontend) {
          try { unlinkSync(DEV_RUN_PIDS); } catch { /* ignore */ }
        } else {
          writeJsonFile(DEV_RUN_PIDS, pidFile);
        }
      }
    }

    // Clean session so the idempotent shortcut won't trigger with stale credentials
    if (stopped.length > 0) {
      try { unlinkSync(DEV_SESSION); } catch { /* ignore if absent */ }
    }

    const result = { stopped, skipped };

    if (options.json) {
      process.stdout.write(JSON.stringify(result) + '\n');
      return;
    }

    if (stopped.length > 0) for (const s of stopped) logger.success(`Stopped: ${s}`);
    if (skipped.length > 0) for (const s of skipped) logger.info(`Skipped: ${s}`);
    if (stopped.length === 0 && skipped.length === 0) logger.info('Nothing to stop.');
  });

// ── Subcommand: ss dev start ───────────────────────────────────────────────
// Launch the backend and/or frontend DETACHED so they outlive this turn/session
// (see src/lib/dev-launch.ts for why a WMI-created process is used instead of a
// plain background spawn). Returns immediately — the readiness wait stays in the
// /dev-start skill's curl loop, so this command stays non-blocking.

devCommand
  .command('start')
  .description('Launch backend and/or frontend as detached processes that outlive the session')
  .option('--backend-only', 'Launch only the backend')
  .option('--frontend-only', 'Launch only the frontend')
  .option('--visible', 'Show a console window per server (live output) instead of hidden + log files')
  .option('--json', 'Output as JSON')
  .action(async (options: { backendOnly?: boolean; frontendOnly?: boolean; visible?: boolean; json?: boolean }) => {
    interface StartCache {
      api_dir?: string;
      web_dir?: string;
      backend_cmd?: string;
      frontend_cmd?: string;
      api_port?: number;
      web_port?: number;
    }
    const cache = readJsonFile<StartCache>(DEV_RUN_CACHE);

    if (
      !cache?.api_dir || !cache?.web_dir ||
      !cache?.backend_cmd || !cache?.frontend_cmd ||
      !cache?.api_port || !cache?.web_port
    ) {
      const err = 'No cache found. Run ss dev detect first.';
      if (options.json) {
        process.stdout.write(JSON.stringify({ launched: [], skipped: [], errors: [err] }) + '\n');
      } else {
        logger.error(err);
      }
      process.exitCode = 1;
      return;
    }

    const launcherLogDir = LAUNCHER_LOG_DIR; // already absolute (anchored to PROJECT_ROOT)
    try { mkdirSync(launcherLogDir, { recursive: true }); } catch { /* ignore */ }

    interface Launched { side: string; pid: number; log_out: string; log_err: string; }
    const launched: Launched[] = [];
    const skipped: string[] = [];
    const errors: string[] = [];

    const pidFile = readPidFile() ?? { version: 2 };
    pidFile.version = 2;
    // Drop stale records (a dead side) before relaunching so its record can't block.
    const prunedAtStart = pruneDeadSidecar(pidFile);

    interface Spawned { side: 'backend' | 'frontend'; port: number; wrapperPid: number; outLog: string; errLog: string; }
    const spawned: Spawned[] = [];

    const launchSide = (side: 'backend' | 'frontend', workdir: string, cmd: string, port: number): void => {
      // Idempotent: never double-launch a side that is already listening.
      if (getListeningPid(port) !== null) { skipped.push(side); return; }

      const outLog = join(launcherLogDir, `${side}.out.log`);
      const errLog = join(launcherLogDir, `${side}.err.log`);
      const script = buildDetachedLaunchPwsh({ workdir, cmd, outLog, errLog, visible: options.visible });
      // Pass via -EncodedCommand (base64 UTF-16LE) to sidestep Node→PowerShell quote escaping.
      const encoded = Buffer.from(script, 'utf16le').toString('base64');

      const r = spawnSync(
        'powershell',
        ['-NoProfile', '-NonInteractive', '-ExecutionPolicy', 'Bypass', '-EncodedCommand', encoded],
        { encoding: 'utf-8', timeout: 15000 }
      );
      // WMI returns the cmd.exe WRAPPER pid (not the dotnet/node child that binds).
      const wrapperPid = parseInt((r.stdout || '').trim().split(/\r?\n/).pop() || '', 10);
      if (r.status !== 0 || isNaN(wrapperPid)) {
        errors.push(`${side}: launch failed (${(r.stderr || '').trim() || `exit ${r.status}`})`);
        return;
      }
      spawned.push({ side, port, wrapperPid, outLog, errLog });
    };

    const launchBackend = !options.frontendOnly;
    const launchFrontend = !options.backendOnly;

    if (launchBackend) launchSide('backend', cache.api_dir, cache.backend_cmd, cache.api_port);
    if (launchFrontend) launchSide('frontend', cache.web_dir, cache.frontend_cmd, cache.web_port);

    // Capture the REAL listening child PID for each freshly-spawned side (bounded,
    // best-effort). A miss (very slow first build) just leaves listenPid=null — NOT
    // an error: `ss dev wait` is the readiness gate. This capture is what makes
    // ownership a strong-identity check (status/summary), defeating the "foreign
    // process on our dead port shows ✓ up" bug.
    if (spawned.length > 0) {
      const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));
      const deadline = Date.now() + 6000;
      const captured = new Map<string, number>();
      while (captured.size < spawned.length && Date.now() < deadline) {
        refreshNetstatSnapshot();
        for (const s of spawned) {
          if (captured.has(s.side)) continue;
          const pid = getListeningPid(s.port);
          if (pid !== null) captured.set(s.side, pid);
        }
        if (captured.size < spawned.length) await sleep(300);
      }
      const startedAt = new Date().toISOString();
      for (const s of spawned) {
        const listenPid = captured.get(s.side) ?? null;
        launched.push({ side: s.side, pid: listenPid ?? s.wrapperPid, log_out: s.outLog, log_err: s.errLog });
        pidFile[s.side] = { wrapperPid: s.wrapperPid, listenPid, port: s.port, started_at: startedAt, log_out: s.outLog, log_err: s.errLog };
      }
    }

    if (spawned.length > 0 || prunedAtStart) {
      if (pidFile.backend || pidFile.frontend) writeJsonFile(DEV_RUN_PIDS, pidFile);
      else { try { unlinkSync(DEV_RUN_PIDS); } catch { /* ignore */ } }
    }

    const result = { launched, skipped, launcher_log_dir: launcherLogDir, errors };

    if (options.json) {
      process.stdout.write(JSON.stringify(result) + '\n');
      if (errors.length > 0) process.exitCode = 1;
      return;
    }

    for (const l of launched) logger.success(`Launched ${l.side} (pid ${l.pid}) → ${l.log_out}`);
    for (const s of skipped) logger.info(`Skipped ${s} (already running)`);
    for (const e of errors) logger.error(e);
    if (launched.length === 0 && skipped.length === 0 && errors.length === 0) {
      logger.info('Nothing to launch.');
    }
    if (errors.length > 0) process.exitCode = 1;
  });

// ── Subcommand: ss dev up ──────────────────────────────────────────────────
// The DETERMINISTIC orchestrator: runs the whole /dev-start workflow in ONE
// invocation (detect → [normalize-https] → [deps] → [restart: stop+build] → start →
// wait → cartouche), driven by the pure `decideNextAction` state machine. It reuses
// the proven granular subcommands (re-invoked in-process) — zero re-implementation,
// zero regression surface. The /dev-start skill collapses to `ss dev up <flags>
// --json` + handling one `needs_input` envelope. Emits a single { status } result:
//   ok → 0 (cartouche printed; 1 if nothing came up) · needs_input → 0 (a human must
//   pick ports/SQL; NOT a failure) · error → 2 (no dir / npm / compile / configure).

interface UpDetect {
  api_dir?: string; web_dir?: string;
  api_port?: number; web_port?: number;
  launch_profile?: string;
  config_status?: 'ok' | 'mismatch' | 'occupied';
  local_config_missing?: boolean;
  connection_string?: string;
  sql_server?: string;
  sql_database?: string;
  admin_email?: string | null;
  is_default_branch?: boolean;
  legacy_https_profile?: boolean;
  frontend_deps_ready?: boolean;
  git_branch?: string | null;
  proposed_ports?: { api: number; web: number } | null;
  mismatch?: { api_port: number; vite_port: number; launch_profile: string; vite_source: string | null } | null;
  api_busy_pid?: number | null;
  web_busy_pid?: number | null;
  cached_at?: string;
  source_files?: FileMeta[];
  cache_max_age_days?: number;
}

/** Re-invoke this very CLI for a granular `ss dev` subcommand, capturing its output. */
function runDevSub(args: string[], timeoutMs: number): { code: number; stdout: string; stderr: string } {
  const r = spawnSync(process.execPath, [process.argv[1], 'dev', ...args], { encoding: 'utf-8', timeout: timeoutMs });
  return { code: r.status ?? 1, stdout: r.stdout ?? '', stderr: r.stderr ?? '' };
}

/** Parse the last JSON line of a child's stdout (subcommands emit one JSON line with --json). */
function parseJsonLine<T>(stdout: string): T | null {
  const lines = stdout.trim().split(/\r?\n/);
  for (let i = lines.length - 1; i >= 0; i--) {
    const s = lines[i].trim();
    if (!s.startsWith('{') && !s.startsWith('[')) continue;
    try { return JSON.parse(s) as T; } catch { /* keep looking */ }
  }
  return null;
}

/** Incremental (or clean) backend build for `--restart`. Fail-fast: returns the
 *  compiler tail on a non-zero exit so the orchestrator can surface it. */
function runDotnetBuild(apiDir: string, webDir: string | null, clean: boolean, backendOnly: boolean): { ok: boolean; output: string } {
  const run = (args: string[]) => spawnSync('dotnet', args, { cwd: apiDir, encoding: 'utf-8', timeout: 320000, shell: true });
  if (clean) {
    run(['clean']); // best-effort
    if (webDir && !backendOnly) { try { rmSync(join(webDir, 'node_modules', '.vite'), { recursive: true, force: true }); } catch { /* ignore */ } }
  }
  const b = run(['build']);
  if ((b.status ?? 1) !== 0) {
    const out = `${b.stdout || ''}\n${b.stderr || ''}`.trim();
    return { ok: false, output: out.split(/\r?\n/).slice(-40).join('\n') };
  }
  return { ok: true, output: '' };
}

devCommand
  .command('up')
  .description('Run the whole dev workflow in one deterministic pass (detect → start → wait → cartouche)')
  .option('--restart', 'Stop, rebuild (incremental), relaunch')
  .option('--clean', 'With --restart: full clean rebuild + clear the Vite cache')
  .option('--reset', 'Force a fresh detect (invalidate cache)')
  .option('--backend-only', 'Only the backend')
  .option('--frontend-only', 'Only the frontend')
  .option('--json', 'Output the result envelope as JSON')
  .action(async (options: { restart?: boolean; clean?: boolean; reset?: boolean; backendOnly?: boolean; frontendOnly?: boolean; json?: boolean }) => {
    const flags: UpFlags = {
      restart: options.restart, clean: options.clean, reset: options.reset,
      backendOnly: options.backendOnly, frontendOnly: options.frontendOnly,
    };
    const localFlags: UpFlags = { ...flags };
    const scopeArgs = [
      ...(flags.backendOnly ? ['--backend-only'] : []),
      ...(flags.frontendOnly ? ['--frontend-only'] : []),
    ];

    const emit = (env: Record<string, unknown>, exitCode: number): void => {
      if (options.json) process.stdout.write(JSON.stringify(env) + '\n');
      else if (typeof env.cartouche === 'string' && env.cartouche) process.stdout.write(env.cartouche);
      else if (typeof env.message === 'string') logger.error(env.message);
      process.exitCode = exitCode;
    };

    const renderCartouche = (restarted: boolean): { text: string; anythingUp: boolean } => {
      const r = runDevSub(['summary', ...(restarted ? ['--restarted'] : [])], 30000);
      return { text: r.stdout, anythingUp: r.code === 0 };
    };
    const doLaunch = (): { launched: unknown[]; skipped: unknown[]; errors: string[] } => {
      const r = runDevSub(['start', ...scopeArgs, '--json'], 40000);
      const res = parseJsonLine<{ launched?: unknown[]; skipped?: unknown[]; errors?: string[] }>(r.stdout);
      return { launched: res?.launched ?? [], skipped: res?.skipped ?? [], errors: res?.errors ?? [] };
    };
    const doWait = (): unknown =>
      flags.frontendOnly ? null : parseJsonLine(runDevSub(['wait', '--json'], 70000).stdout);

    // Backfill the sidecar's listenPid from the LIVE listener once this pass has positive
    // ownership proof — closes the cold-boot race where `start`'s post-launch poll expires
    // before a slow first `dotnet run` binds the port (listenPid stays null → the strict
    // cartouche disowns our own healthy backend → false ✗ DOWN + exit 1 → restart spiral).
    // Pure eligibility (listenPidBackfillTargets) decides; here we only read live PIDs and
    // persist, so every later `ss dev summary`/`status` is correct too. Never a blind adopt.
    const backfillListenPids = (launchedJson: unknown[], wait: unknown): void => {
      const cache = readJsonFile<{ api_port?: number; web_port?: number }>(DEV_RUN_CACHE);
      if (!cache?.api_port || !cache?.web_port) return;
      const pf = readPidFile();
      if (!pf) return;
      const launchedSides = new Set(
        launchedJson.map((l) => (l as { side?: string })?.side).filter((s): s is string => !!s)
      );
      const targets = listenPidBackfillTargets({
        backendReady: (wait as { ready?: boolean } | null)?.ready === true,
        frontendLaunched: launchedSides.has('frontend'),
        backendListenPid: pf.backend?.listenPid,
        frontendListenPid: pf.frontend?.listenPid,
      });
      let mutated = false;
      let refreshed = false;
      const fill = (side: 'backend' | 'frontend', port: number): void => {
        const rec = pf[side];
        if (!rec) return;
        const live = getListeningPid(port, !refreshed); // one fresh netstat, then reuse
        refreshed = true;
        if (live !== null) { rec.listenPid = live; mutated = true; }
      };
      if (targets.backend) fill('backend', cache.api_port);
      if (targets.frontend) fill('frontend', cache.web_port);
      if (mutated) writeJsonFile(DEV_RUN_PIDS, pf);
    };

    // ── Initial state, computed in-process (no spawn) ─────────────────────
    const cache0 = readJsonFile<UpDetect>(DEV_RUN_CACHE);
    const stale0 = computeCacheStale(cache0);
    let occApi = false, occWeb = false;
    if (cache0?.api_port && cache0?.web_port) {
      const o = classifyOccupancy({
        apiPort: cache0.api_port, webPort: cache0.web_port,
        apiBusyPid: getListeningPid(cache0.api_port), webBusyPid: getListeningPid(cache0.web_port),
        pids: toSidecarPids(readPidFile()),
      });
      occApi = o.apiBusyBySelf; occWeb = o.webBusyBySelf;
    }
    let state: OrchestratorState = {
      cacheExists: !!(cache0?.api_port && cache0?.web_port),
      cacheStale: stale0.stale,
      hasConfigStatus: cache0?.config_status !== undefined,
      backendRunning: occApi,
      frontendRunning: occWeb,
      configStatus: cache0?.config_status,
      localConfigMissing: cache0?.local_config_missing,
      legacyHttpsProfile: cache0?.legacy_https_profile,
      frontendDepsReady: cache0?.frontend_deps_ready,
    };
    let lastDetect: UpDetect | null = cache0 ?? null;

    // ── Drive the machine (bounded — detect→normalize→detect is finite) ───
    for (let guard = 0; guard < 8; guard++) {
      const action = decideNextAction(state, localFlags);

      if (action.kind === 'idempotent_summary') {
        const c = renderCartouche(false);
        emit({ status: 'ok', mode: 'idempotent', cartouche: c.text }, c.anythingUp ? 0 : 1);
        return;
      }

      if (action.kind === 'detect') {
        const r = runDevSub(['detect', ...(localFlags.reset ? ['--reset'] : []), '--json'], 45000);
        const det = parseJsonLine<UpDetect & { error?: string }>(r.stdout);
        if (!det || det.error || !det.api_port) {
          emit({ status: 'error', phase: 'detect', message: det?.error ?? (r.stderr.trim() || 'detect failed') }, 2);
          return;
        }
        lastDetect = det;
        localFlags.reset = false;
        // After a detect the ports may have changed; force the launch path (start
        // idempotently skips a side already listening) rather than re-shortcutting
        // against stale running-state computed on the old ports.
        state = {
          cacheExists: true, cacheStale: false,
          hasConfigStatus: det.config_status !== undefined,
          backendRunning: false, frontendRunning: false,
          configStatus: det.config_status,
          localConfigMissing: det.local_config_missing,
          legacyHttpsProfile: det.legacy_https_profile,
          frontendDepsReady: det.frontend_deps_ready,
        };
        continue;
      }

      if (action.kind === 'normalize_https') {
        const spec = JSON.stringify({ apiPort: lastDetect?.api_port, webPort: lastDetect?.web_port });
        const cfg = parseJsonLine<{ success?: boolean; error?: string }>(runDevSub(['configure', '--json', '--spec', spec], 30000).stdout);
        if (!cfg?.success) {
          emit({ status: 'error', phase: 'configure', message: cfg?.error ?? 'https normalize failed' }, 2);
          return;
        }
        localFlags.reset = true; state.legacyHttpsProfile = false; state.cacheStale = true; // → detect next
        continue;
      }

      if (action.kind === 'seed_local_config') {
        // First run on this branch: no appsettings.Local.json. Rather than silently
        // writing it, surface a PROPOSAL the user confirms/edits — the mandatory app
        // login (Security.InitialAdmin) and the per-branch database are too important
        // to guess. The base connection string gives the default SQL server + DB
        // (per-branch on a non-default branch); the admin login defaults to the
        // project's existing one or DEFAULT_ADMIN_EMAIL, with a freshly generated
        // strong password. The SKILL renders ONE confirm/edit prompt, calls
        // `ss dev configure` (which writes the file + ensures the DB), then relaunches.
        const base = lastDetect?.connection_string
          ? parseConnectionString(lastDetect.connection_string)
          : { server: '(local)', database: 'SmartStack', useWindowsAuth: true };
        const baseDb = base.database && base.database !== 'unknown' ? base.database : 'SmartStack';
        const targetDb =
          lastDetect?.is_default_branch === true
            ? baseDb
            : deriveDatabaseName(lastDetect?.git_branch ?? null, baseDb);
        const server = lastDetect?.sql_server && lastDetect.sql_server !== 'unknown' ? lastDetect.sql_server : base.server;
        emit(
          { ...buildSeedConfigEnvelope({
            gitBranch: lastDetect?.git_branch ?? null,
            apiPort: lastDetect?.api_port ?? 5142,
            webPort: lastDetect?.web_port ?? 6173,
            server,
            database: targetDb,
            adminEmail: lastDetect?.admin_email || DEFAULT_ADMIN_EMAIL,
            adminPassword: generateStrongPassword(),
            flags,
          }) },
          0
        );
        return;
      }

      if (action.kind === 'needs_input') {
        emit({ ...buildNeedsInputEnvelope({
          reason: action.reason,
          gitBranch: lastDetect?.git_branch ?? null,
          apiPort: lastDetect?.api_port ?? 0, webPort: lastDetect?.web_port ?? 0,
          proposedPorts: lastDetect?.proposed_ports ?? null,
          mismatch: lastDetect?.mismatch ?? null,
          apiBusyPid: lastDetect?.api_busy_pid ?? null, webBusyPid: lastDetect?.web_busy_pid ?? null,
          flags,
        }) }, 0);
        return;
      }

      if (action.kind === 'ensure_deps') {
        const webDir = lastDetect?.web_dir;
        if (webDir) {
          const npm = spawnSync('npm', ['install'], { cwd: webDir, encoding: 'utf-8', timeout: 130000, shell: true });
          if (npm.status !== 0) {
            emit({ status: 'error', phase: 'npm_install', message: `npm install failed in ${webDir}`, output: (npm.stderr || npm.stdout || '').slice(-2000) }, 2);
            return;
          }
        }
        state.frontendDepsReady = true;
        continue;
      }

      if (action.kind === 'restart_cycle') {
        runDevSub(['stop', ...scopeArgs, '--json'], 30000); // "No cache" is non-fatal
        if (action.build !== 'none' && lastDetect?.api_dir) {
          const build = runDotnetBuild(lastDetect.api_dir, lastDetect.web_dir ?? null, action.build === 'clean', flags.backendOnly === true);
          if (!build.ok) {
            emit({ status: 'error', phase: 'build', message: 'backend build failed', compiler_output: build.output }, 2);
            return;
          }
        }
        const s = doLaunch();
        const wait = doWait();
        backfillListenPids(s.launched, wait);
        const c = renderCartouche(true);
        emit({ status: 'ok', mode: 'restart', launched: s.launched, skipped: s.skipped, errors: s.errors, wait, cartouche: c.text }, c.anythingUp ? 0 : 1);
        return;
      }

      // action.kind === 'launch'
      const s = doLaunch();
      const wait = doWait();
      backfillListenPids(s.launched, wait);
      const c = renderCartouche(false);
      emit({ status: 'ok', mode: 'launch', launched: s.launched, skipped: s.skipped, errors: s.errors, wait, cartouche: c.text }, c.anythingUp ? 0 : 1);
      return;
    }

    emit({ status: 'error', phase: 'loop', message: 'orchestrator did not converge' }, 2);
  });
